import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import type { INote } from '@dataTypes/note.type'
import { NoteContextType } from '@dataTypes/note.type'
import { useAppDispatch } from '@store/hooks'
import { toggleSidePanel } from '@store/reducers/uiSlice.reducer'
import { useNotificationToast } from '@context/NotificationToastContext'
import { createFailedToast } from '@utils/notificationToast.util'
import NoteEditorOverlay from './NoteEditorOverlay.component'
import type { NoteEditorOverlayProps } from './NoteEditorOverlay.component'
import type {
  NoteMutationSuccessDetail,
  NoteOverlayOpenSource,
} from './notesFeature.types'

export type NoteEditorOpenPayload = Omit<
  NoteEditorOverlayProps,
  'isOpen' | 'onClose'
> & {
  /** Invoked when the overlay closes (user dismiss or programmatic close). */
  onOverlayWillClose?: () => void
}

type StoredOverlayProps = Omit<
  NoteEditorOpenPayload,
  'onNoteMutationSuccess' | 'onNoteSavedImmediate' | 'onOverlayWillClose'
>

type NoteEditorOverlayApi = {
  /** Returns false when the overlay was not opened (e.g. missing context). */
  openNoteEditor: (payload: NoteEditorOpenPayload) => boolean
  closeNoteEditor: () => void
  updateNoteEditor: (partial: Partial<NoteEditorOpenPayload>) => void
}

const ApiContext = createContext<NoteEditorOverlayApi | null>(null)

type HostContextValue = {
  isOpen: boolean
  stored: StoredOverlayProps | null
  requestClose: () => void
  mutationSuccessRef: React.MutableRefObject<
    ((detail?: NoteMutationSuccessDetail) => void) | undefined
  >
  savedImmediateRef: React.MutableRefObject<((note: INote) => void) | undefined>
}

const HostContext = createContext<HostContextValue | null>(null)

function toStoredPayload(payload: NoteEditorOpenPayload): StoredOverlayProps {
  const copy = { ...payload } as Record<string, unknown>
  delete copy.onNoteMutationSuccess
  delete copy.onNoteSavedImmediate
  delete copy.onOverlayWillClose
  return copy as StoredOverlayProps
}

function withDefaults(stored: StoredOverlayProps): StoredOverlayProps {
  return {
    ...stored,
    patient: stored.patient ?? null,
    contextId: stored.contextId ?? null,
    contextType: stored.contextType ?? null,
    contextName: stored.contextName ?? null,
    contactId: stored.contactId ?? null,
    openFirstNote: stored.openFirstNote ?? false,
    openNoteId: stored.openNoteId ?? null,
    pagePreviousState: stored.pagePreviousState ?? null,
    initialView: stored.initialView ?? 'add',
    openNoteInEditMode: stored.openNoteInEditMode ?? false,
    overlayBackLabel: stored.overlayBackLabel ?? 'Back',
    portalTarget: stored.portalTarget ?? '.page-content',
    hideNotesTable: stored.hideNotesTable ?? true,
  }
}

function hasRequiredOverlayContext(payload: NoteEditorOpenPayload): boolean {
  const hasPatient = Boolean(payload.patient?.patient_id)
  const hasContactContext =
    payload.contextType === NoteContextType.CONTACT &&
    payload.contactId != null &&
    payload.contactId.trim() !== ''
  const hasCallContext =
    payload.contextType === NoteContextType.CALL &&
    payload.contextId != null &&
    payload.contextId.trim() !== ''
  const hasAppointmentContext =
    payload.contextType === NoteContextType.APPOINTMENT &&
    payload.contextId != null &&
    String(payload.contextId).trim() !== ''
  const hasClaimsContext =
    (payload.contextType === NoteContextType.CLAIMS ||
      payload.contextType === NoteContextType.CLAIM_ITEM) &&
    payload.contextId != null &&
    String(payload.contextId).trim() !== ''

  return (
    hasPatient ||
    hasContactContext ||
    hasCallContext ||
    hasAppointmentContext ||
    hasClaimsContext
  )
}

export function NoteEditorOverlayProvider({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement {
  const dispatch = useAppDispatch()
  const { pathname } = useLocation()
  const { showToast } = useNotificationToast()
  const [isOpen, setIsOpen] = useState(false)
  const [stored, setStored] = useState<StoredOverlayProps | null>(null)
  const isOpenRef = useRef(false)
  const prevPathnameRef = useRef<string | null>(null)

  useEffect((): void => {
    isOpenRef.current = isOpen
  }, [isOpen])

  const mutationSuccessRef = useRef<
    ((detail?: NoteMutationSuccessDetail) => void) | undefined
  >(undefined)
  const savedImmediateRef = useRef<((note: INote) => void) | undefined>(
    undefined,
  )
  const overlayWillCloseRef = useRef<(() => void) | undefined>(undefined)

  const closeNoteEditor = useCallback((): void => {
    overlayWillCloseRef.current?.()
    overlayWillCloseRef.current = undefined
    mutationSuccessRef.current = undefined
    savedImmediateRef.current = undefined
    setIsOpen(false)
    setStored(null)
  }, [])

  useEffect((): void => {
    if (
      prevPathnameRef.current != null &&
      prevPathnameRef.current !== pathname &&
      isOpen
    ) {
      closeNoteEditor()
    }
    prevPathnameRef.current = pathname
  }, [pathname, isOpen, closeNoteEditor])

  const openNoteEditor = useCallback(
    (payload: NoteEditorOpenPayload): boolean => {
      if (!hasRequiredOverlayContext(payload)) {
        showToast(
          createFailedToast(
            'Cannot open notes',
            'Patient or note context is required to open the note editor.',
          ),
        )
        return false
      }

      const {
        onNoteMutationSuccess,
        onNoteSavedImmediate,
        onOverlayWillClose,
      } = payload
      const overlayOpenSource = (payload.pagePreviousState?.overlayOpenSource ??
        'other-notes-entry') as NoteOverlayOpenSource
      if (overlayOpenSource !== 'patient-sidepanel-notes') {
        dispatch(toggleSidePanel(false))
      }
      mutationSuccessRef.current = onNoteMutationSuccess
      savedImmediateRef.current = onNoteSavedImmediate
      overlayWillCloseRef.current = onOverlayWillClose
      const nextStored = withDefaults(toStoredPayload(payload))
      setStored((prev) => {
        if (
          isOpenRef.current &&
          prev != null &&
          prev.hideNotesTable === false &&
          nextStored.hideNotesTable === false &&
          prev.patient?.patient_id != null &&
          prev.patient.patient_id === nextStored.patient?.patient_id
        ) {
          return prev
        }
        return nextStored
      })
      setIsOpen(true)
      return true
    },
    [dispatch, showToast],
  )

  const updateNoteEditor = useCallback(
    (partial: Partial<NoteEditorOpenPayload>): void => {
      if (partial.onNoteMutationSuccess !== undefined) {
        mutationSuccessRef.current = partial.onNoteMutationSuccess
      }
      if (partial.onNoteSavedImmediate !== undefined) {
        savedImmediateRef.current = partial.onNoteSavedImmediate
      }
      if (partial.onOverlayWillClose !== undefined) {
        overlayWillCloseRef.current = partial.onOverlayWillClose
      }

      const rest = { ...partial } as Record<string, unknown>
      delete rest.onNoteMutationSuccess
      delete rest.onNoteSavedImmediate
      delete rest.onOverlayWillClose

      const serialPatch = Object.fromEntries(
        Object.entries(rest).filter(([, v]) => v !== undefined),
      ) as Partial<StoredOverlayProps>

      if (Object.keys(serialPatch).length === 0) return

      setStored((prev) => (prev == null ? prev : { ...prev, ...serialPatch }))
    },
    [],
  )

  const api = useMemo(
    (): NoteEditorOverlayApi => ({
      openNoteEditor,
      closeNoteEditor,
      updateNoteEditor,
    }),
    [openNoteEditor, closeNoteEditor, updateNoteEditor],
  )

  const hostValue = useMemo(
    (): HostContextValue => ({
      isOpen,
      stored,
      requestClose: closeNoteEditor,
      mutationSuccessRef,
      savedImmediateRef,
    }),
    [isOpen, stored, closeNoteEditor],
  )

  return (
    <HostContext.Provider value={hostValue}>
      <ApiContext.Provider value={api}>{children}</ApiContext.Provider>
    </HostContext.Provider>
  )
}

export function useNoteEditorOverlay(): NoteEditorOverlayApi {
  const ctx = useContext(ApiContext)
  if (ctx == null) {
    throw new Error(
      'useNoteEditorOverlay must be used within NoteEditorOverlayProvider',
    )
  }
  return ctx
}

/** Renders the single app-wide note editor overlay (place next to `<Routes />` in AppRoutes). */
export function NoteEditorOverlayHost(): React.ReactElement | null {
  const host = useContext(HostContext)
  const { pathname } = useLocation()

  if (host == null || !host.isOpen || host.stored == null) {
    return null
  }

  const disablePageContentPortal =
    pathname === '/forms/kiosk' ||
    pathname === '/forms/public' ||
    pathname === '/login'

  const { portalTarget: requestedPortal, ...restStored } = host.stored
  const portalTarget = disablePageContentPortal ? undefined : requestedPortal

  return (
    <NoteEditorOverlay
      isOpen={host.isOpen}
      onClose={host.requestClose}
      {...restStored}
      portalTarget={portalTarget}
      onNoteMutationSuccess={(detail) =>
        host.mutationSuccessRef.current?.(detail)
      }
      onNoteSavedImmediate={(note) => host.savedImmediateRef.current?.(note)}
    />
  )
}
